// Pure helpers voor het parttime-/vast weekrooster (spec parttime-rooster-verlof-
// verzuim). Eén waarheid voor "hoeveel uur zou deze persoon op deze weekdag
// werken". Werkt op `number` (consistent met berekenDaguren); de API-laag
// (`contractUrenOpDatum`) levert Decimal voor exacte sommen. 0-urendagen tellen
// 0 bij; weekend kan via een ingevuld rooster wel uren hebben (ploegen/weekend).

export interface WeekRooster {
  maandag: number
  dinsdag: number
  woensdag: number
  donderdag: number
  vrijdag: number
  zaterdag: number
  zondag: number
}

// JS Date.getDay()/getUTCDay(): 0 = zondag .. 6 = zaterdag. WEEKDAG_VELDEN mapt
// die index op het juiste rooster-veld zodat een Date direct opgezocht kan worden.
const WEEKDAG_VELDEN: ReadonlyArray<keyof WeekRooster> = [
  'zondag',
  'maandag',
  'dinsdag',
  'woensdag',
  'donderdag',
  'vrijdag',
  'zaterdag',
]

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

function nonNegatief(u: number): number {
  return Number.isFinite(u) && u > 0 ? round2(u) : 0
}

/**
 * Roosteruren voor één weekdag. `weekdag` = JS getDay (0 = zondag .. 6 = zaterdag)
 * of een `Date` (waarvan de UTC-weekdag gebruikt wordt - onze datums zijn
 * UTC-middernacht van de NL-kalenderdag). Een niet-ingevulde/0-dag levert 0.
 */
export function contractUrenVoorWeekdag(rooster: WeekRooster, weekdag: number | Date): number {
  const idx = typeof weekdag === 'number' ? weekdag : weekdag.getUTCDay()
  const veld = WEEKDAG_VELDEN[((idx % 7) + 7) % 7]
  return nonNegatief(rooster[veld])
}

/**
 * Per-dag (ma..zo volgorde) + weektotaal van een rooster. Gebruikt voor de UI
 * (weektotaal + per-dag preview) en de som-validatie tegen de contracturen.
 */
export function getWeekRoosterUren(rooster: WeekRooster): { perDag: number[]; weektotaal: number } {
  const perDag = [
    rooster.maandag,
    rooster.dinsdag,
    rooster.woensdag,
    rooster.donderdag,
    rooster.vrijdag,
    rooster.zaterdag,
    rooster.zondag,
  ].map(nonNegatief)
  const weektotaal = round2(perDag.reduce((som, u) => som + u, 0))
  return { perDag, weektotaal }
}
